import {
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Checkbox as MuiCheckbox,
  CheckboxProps as MuiCheckboxProps,
  Tooltip,
} from '@mui/material'
import { ReactElement, ReactNode } from 'react'
import { Field, useField } from 'react-final-form'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { TYPOGRAPHY_MIXIN } from 'src/shared/constants/styling-constants'
import { InfoIcon } from 'src/shared/icons/Icons'
import styled, { css } from 'styled-components'

export interface FeatureBaseCheckboxData {
  label?: string
  value?: unknown
  disabled?: boolean
  indeterminate?: boolean
  tooltip?: string
}

export interface FeatureBaseCheckboxesProps extends Partial<Omit<MuiCheckboxProps, 'onChange'>> {
  name: string
  data: FeatureBaseCheckboxData | FeatureBaseCheckboxData[]
  label?: ReactNode
  required?: boolean
  validate?: any
  children?: ReactNode
  $withTextField?: boolean
}

const config = {
  subscription: {
    error: true,
    submitError: true,
    dirtySinceLastSubmit: true,
    touched: true,
    modified: true,
  },
}

const FormLabelStyled = styled(FormLabel)`
  padding: ${({ theme }) => theme.spacing(1.5)} ${({ theme }) => theme.spacing(2)};
  color: ${({ theme }) => theme.colors.text.primary};
  ${TYPOGRAPHY_MIXIN.subtitle2};
`

const DivStyled = styled('div')<{
  $disabled?: boolean
  $withTextField?: boolean
  $noLabel?: boolean
  $paddingInline?: number
}>`
  padding: 0
    ${({ $paddingInline, theme }) => ($paddingInline !== undefined ? theme.spacing($paddingInline) : theme.spacing(2))};
  display: flex;
  align-items: center;
  gap: ${({ $withTextField, theme }) => ($withTextField ? 0 : theme.spacing(1))};

  &:hover {
    background-color: ${({ $disabled, theme }) => ($disabled ? 'none' : theme.colors.action.hover)};
  }

  ${({ $withTextField }) =>
    $withTextField &&
    css`
      align-items: flex-start;

      & > .MuiFormControlLabel-root {
        height: 3.5rem;
      }

      & > .MuiSvgIcon-root {
        position: relative;
        top: 1rem;
      }

      &:hover {
        background-color: transparent;
      }

      label {
        cursor: default;
      }
    `}
  ${({ $noLabel }) =>
    $noLabel &&
    css`
      display: inline-block;

      &:hover {
        background-color: transparent;
      }

      & > .MuiFormControlLabel-root {
        margin-left: 0;
        cursor: default;
      }
    `}
`

const FormControlLabelStyled = styled(FormControlLabel)`
  height: 3rem;
  margin-right: 0;
  flex-grow: 1;
`

const InfoIconStyled = styled(InfoIcon)`
  font-size: 1.25rem;
`

export const FeatureBaseCheckboxGroupField = (props: FeatureBaseCheckboxesProps): ReactElement => {
  const { required, label, data, name, validate, children, $withTextField, ...rest } = props

  const itemsData = Array.isArray(data) ? data : [data]
  const single = !Array.isArray(data)
  const field = useField(name, config)
  const { touched, error: errorObject } = field.meta
  const { getMessage } = useMessageSource()
  const error = errorObject && getMessage(errorObject.errorKey, errorObject.params)
  const invalid = Boolean(touched && error)

  return (
    <FormControl required={required} error={invalid} fullWidth>
      {label ? <FormLabelStyled>{label}</FormLabelStyled> : <></>}
      <FormGroup>
        {itemsData.map((item: FeatureBaseCheckboxData, idx: number) => (
          <div key={idx}>
            <DivStyled key={idx} $disabled={item.disabled || rest.disabled} $withTextField={$withTextField}>
              <FormControlLabelStyled
                key={idx}
                name={name}
                label={item.label || ''}
                value={single ? undefined : item.value}
                disabled={item.disabled || rest.disabled}
                control={
                  <Field
                    type="checkbox"
                    name={name}
                    validate={validate}
                    render={({ input: { name, value, onChange, checked, ...restInput } }) => (
                      <MuiCheckbox
                        name={name}
                        value={value}
                        onChange={onChange}
                        checked={checked}
                        disabled={item.disabled || rest.disabled}
                        inputProps={{ required, ...restInput }}
                        indeterminate={item.indeterminate}
                        {...rest}
                      />
                    )}
                  />
                }
              />
              {item.tooltip && (
                <Tooltip title={item.tooltip} placement="right">
                  <InfoIconStyled />
                </Tooltip>
              )}
              {children}
            </DivStyled>
          </div>
        ))}
      </FormGroup>
    </FormControl>
  )
}
